import { isBooleanFactKey, type PropertyFactKey } from 'src/services/api/properties/property-fact-keys';
import {
    isBooleanPropertyFact,
    isStringPropertyFact,
    type PropertyFactValue,
} from 'src/services/api/properties/property-fact-values';
import type { IPropertyFact } from 'src/services/api/properties/types';

// react-hook-form rows hold strings for every fact; boolean facts use these two and are
// converted back to real JSON booleans on the way to the API.
export const BOOLEAN_FORM_TRUE = 'true';
export const BOOLEAN_FORM_FALSE = 'false';

export const BOOLEAN_FACT_OPTIONS = [
    { value: BOOLEAN_FORM_TRUE, label: 'Yes' },
    { value: BOOLEAN_FORM_FALSE, label: 'No' },
];

/**
 * Whether the editor can render and save this fact. Facts stored as numbers, objects or
 * arrays have no input yet, so they stay untouched rather than being flattened to text.
 */
export function isEditableFact(fact: IPropertyFact): boolean {
    return isStringPropertyFact(fact) || isBooleanPropertyFact(fact);
}

/**
 * The stored fact value as the string a form row holds, or null when the fact isn't
 * editable.
 */
export function factToFormValue(fact: IPropertyFact): string | null {
    let formValue: string | null = null;
    if (isStringPropertyFact(fact)) {
        formValue = fact.value;
    } else if (isBooleanPropertyFact(fact)) {
        formValue = fact.value ? BOOLEAN_FORM_TRUE : BOOLEAN_FORM_FALSE;
    }
    return formValue;
}

/**
 * A form row's string turned back into the JSON value the API should store: a real
 * boolean for boolean keys, trimmed text otherwise.
 */
export function formValueToApiValue(key: PropertyFactKey, formValue: string): PropertyFactValue {
    return isBooleanFactKey(key) ? formValue === BOOLEAN_FORM_TRUE : formValue.trim();
}

/**
 * Validation message for a row's value, or null when it's valid. Boolean keys must be
 * one of the two picker options so a half-filled row can't post an accidental `false`.
 */
export function getFactValueError(key: PropertyFactKey | '', formValue: string): string | null {
    let error: string | null = null;
    if (isBooleanFactKey(key)) {
        if (formValue !== BOOLEAN_FORM_TRUE && formValue !== BOOLEAN_FORM_FALSE) {
            error = 'Select Yes or No';
        }
    } else if (formValue.trim().length === 0) {
        error = 'Value is required';
    }
    return error;
}
